import type { QueryClient } from '@tanstack/react-query';
import { DatasetType, ResourceType } from '@cotera/api';
import type {
  TenantedClient,
  ResourceGetResponse as ResourceOutput,
  FolderItemsResponse as ResourceFolderItemsOutput,
  DatasetOutput,
  DatasetArtifactOutput,
} from '@cotera/client/app/etc/api/typed-client';
import {
  Agent,
  Dataset,
  Document,
  Folder,
  type FolderItemRef,
  type SerializedDataset,
  type SerializedDatasetArtifact,
  type WorkspaceDomainLoader,
  resourceViewOfItem,
} from '@cotera/sdk/core';
import { queryKeys } from './query-cache-keys';

export type CreateWorkspaceDomainLoaderParams = {
  queryClient: QueryClient;
  orgId: string;
  resource: Pick<TenantedClient['resource'], 'get' | 'folder'>;
};

async function fetchResourceOutput(
  queryClient: QueryClient,
  orgId: string,
  resource: Pick<TenantedClient['resource'], 'get'>,
  type: ResourceType,
  id: string
): Promise<ResourceOutput | null> {
  const key = queryKeys.workspaceDomain.resource({ orgId, type, id });
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async (): Promise<ResourceOutput | null> => {
      const res = await resource.get({ type, id });
      if (res.isErr()) {
        const appErr = res.error.t === 'application' ? res.error.err : null;
        if (
          appErr !== null &&
          (appErr.errorType === 'NotFound' || appErr.errorType === 'Forbidden')
        ) {
          return null;
        }
        throw new Error(appErr?.message ?? 'Failed to load resource');
      }
      return res.value;
    },
    staleTime: 60_000,
  });
}

async function fetchWire<const K extends ResourceOutput['type']>(
  queryClient: QueryClient,
  orgId: string,
  resource: Pick<TenantedClient['resource'], 'get'>,
  type: ResourceType,
  id: string,
  kind: K
): Promise<Extract<ResourceOutput, { type: K }> | null> {
  const output = await fetchResourceOutput(
    queryClient,
    orgId,
    resource,
    type,
    id
  );
  if (output === null || output.type !== kind) {
    return null;
  }
  return output as Extract<ResourceOutput, { type: K }>;
}

/**
 * The typed client mirrors dataset kinds as string literals and dates as
 * `Date | string`, while {@link SerializedDataset} wants the {@link DatasetType}
 * enum and ISO strings. Neither shape is wrong — the wire carries both — so the
 * conversion lives here rather than tightening a typed-client interface that a
 * dozen dataset-page call sites compare against string literals.
 */
const toDatasetType = (type: DatasetOutput['type']): DatasetType => {
  switch (type) {
    case 'csv':
      return DatasetType.Csv;
    case 'manual':
      return DatasetType.Manual;
    // 'warehouse' is the legacy Snowflake value kept for rows written before
    // the per-vendor split; 'snowflake' is its restamped form.
    case 'warehouse':
    case 'snowflake':
      return DatasetType.Snowflake;
    case 'postgres':
      return DatasetType.Postgres;
    case 'bigquery':
      return DatasetType.BigQuery;
    default:
      // Deliberately not `Assert.unreachable`. This parses a value off the
      // wire, and a server that has rolled forward past this bundle can send a
      // kind it has never heard of — a tab left open across the deploy that
      // introduced one, most obviously. Throwing here takes the whole workspace
      // load down for an unknown string; falling back to the warehouse kind
      // degrades one dataset's label instead.
      //
      // `type` is `never` on this branch, so the assertion below is what keeps
      // the switch exhaustive: adding a kind to the wire union without a case
      // here is still a compile error.
      assertHandledEveryDatasetType(type);
      return DatasetType.Snowflake;
  }
};

/**
 * Compile-time exhaustiveness for {@link toDatasetType} without the runtime
 * throw: reaching this with a real value means the wire carried a kind this
 * bundle predates, which is a rollout state, not a bug.
 */
const assertHandledEveryDatasetType = (_type: never): void => {};

const toIso = (value: Date | string): string =>
  typeof value === 'string' ? value : value.toISOString();

const toIsoOrNull = (value: Date | string | null): string | null =>
  value === null ? null : toIso(value);

const toSerializedArtifact = (
  artifact: DatasetArtifactOutput
): SerializedDatasetArtifact => ({
  id: artifact.id,
  datasetId: artifact.datasetId,
  pointer: artifact.pointer,
  hash: artifact.hash,
  metadata: artifact.metadata,
  expiresAt: toIsoOrNull(artifact.expiresAt),
  createdAt: toIso(artifact.createdAt),
  updatedAt: toIso(artifact.updatedAt),
  lastAccessedAt: toIsoOrNull(artifact.lastAccessedAt),
});

const toSerializedDataset = (
  dataset: DatasetOutput,
  folderId: string | null
): SerializedDataset => ({
  id: dataset.id,
  orgId: dataset.orgId,
  ownerId: dataset.ownerId,
  type: toDatasetType(dataset.type),
  name: dataset.name,
  originalFilename: dataset.originalFilename,
  mimeType: dataset.mimeType,
  sizeBytes: dataset.sizeBytes,
  status: dataset.status,
  createdAt: toIso(dataset.createdAt),
  updatedAt: toIso(dataset.updatedAt),
  folderId,
  artifacts: (dataset.artifacts ?? []).map(toSerializedArtifact),
});

function mapFolderItemsToRefs(
  items: ResourceFolderItemsOutput['items']
): readonly FolderItemRef[] {
  return items.map((item, position) => {
    const view = resourceViewOfItem(item);
    return { entityType: view.kind.type, entityId: view.id, position };
  });
}

async function fetchFolderItemRefs(
  queryClient: QueryClient,
  orgId: string,
  resource: Pick<TenantedClient['resource'], 'folder'>,
  folderId: string
): Promise<readonly FolderItemRef[]> {
  const key = queryKeys.workspaceDomain.folderItems({ orgId, folderId });
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await resource.folder.getFolderItems({
        folderId,
        limit: 100,
        offset: 0,
        sort: 'position',
        sortDir: 'asc',
      });
      if (res.isErr()) {
        return [];
      }
      return mapFolderItemsToRefs(res.value.items);
    },
    staleTime: 30_000,
  });
}

export function createWorkspaceDomainLoader(
  params: CreateWorkspaceDomainLoaderParams
): WorkspaceDomainLoader {
  const { queryClient, orgId, resource } = params;

  const loader: WorkspaceDomainLoader = {
    loadAgent: async (id) => {
      const o = await fetchWire(
        queryClient,
        orgId,
        resource,
        ResourceType.Agent,
        id,
        'agent'
      );
      return o === null ? null : Agent.fromResourceOutput(o, loader);
    },
    loadDocument: async (id) => {
      const o = await fetchWire(
        queryClient,
        orgId,
        resource,
        ResourceType.Document,
        id,
        'document'
      );
      return o === null ? null : Document.fromResourceOutput(o, loader);
    },
    loadFolder: async (id) => {
      const o = await fetchWire(
        queryClient,
        orgId,
        resource,
        ResourceType.Folder,
        id,
        'folder'
      );
      if (o === null) {
        return null;
      }
      return Folder.fromResourceOutput(o, orgId, {
        loadParent: async () =>
          o.folder.parentId === null
            ? null
            : loader.loadFolder(o.folder.parentId),
        loadItems: async () =>
          fetchFolderItemRefs(queryClient, orgId, resource, o.folder.id),
      });
    },
    loadDataset: async (id) => {
      const o = await fetchWire(
        queryClient,
        orgId,
        resource,
        ResourceType.Dataset,
        id,
        'dataset'
      );
      if (o === null) {
        return null;
      }
      return Dataset.fromSerialized(
        toSerializedDataset(o.dataset, o.meta?.folderId ?? null)
      );
    },
  };

  return loader;
}
